import _ from 'lodash';

import SessionStorageAx  from 'app/actions/session-storage';
import utils             from 'app/actions/utils';
import analytics         from 'app/analytics';
import millieApi         from 'app/apis/millie';
import config            from 'app/config';
import history           from 'app/history';
import paths             from 'app/paths';
import AuthSlx           from 'app/selectors/auth';
import RoutingSlx        from 'app/selectors/routing';
import SessionStorageSlx from 'app/selectors/session-storage';

const Types = {
  SIGN_UP: 'AUTH_SIGN_UP',
  PW_LOGIN: 'AUTH_PW_LOGIN',
  SSO_LOGIN: 'AUTH_SSO_LOGIN',
  LOGOUT: 'AUTH_LOGOUT',
  GET_CURRENT_USER: 'AUTH_GET_CURRENT_USER',
  SET_CURRENT_USER: 'AUTH_SET_CURRENT_USER',
  RESEND_EV: 'AUTH_RESEND_EV',
  SEND_PW_RESET: 'AUTH_SEND_PW_RESET',
  RESEND_EMP_INVITE: 'AUTH_RESEND_EMP_INVITE',
  EMP_EMAIL_SIGNUP: 'AUTH_EMP_EMAIL_SIGNUP',
};

const Actions = {

  redirectAfterLogin: () => (dispatch, getState) => {
    process.nextTick(() => {
      const state = getState();
      const currentUser = AuthSlx.currentUser(state);
      if (!currentUser) return history.push(paths.home);
      // first check emailVerified
      const { emailVerified } = currentUser;
      if (!emailVerified) return history.push(paths.verifyEmailPage);
      // next look for a postLoginUrl
      const postLoginUrl = SessionStorageSlx.postLoginUrl(state);
      if (postLoginUrl) {
        history.push(postLoginUrl);
        dispatch(SessionStorageAx.setPostLoginUrl(null));
        return;
      }
      // next look for a donationTarget
      const donationTarget = SessionStorageSlx.donationTarget(state);
      if (donationTarget) {
        if (currentUser.balanceInCents) {
          history.push(paths.nonprofit(donationTarget));
          dispatch(SessionStorageAx.setDonationTarget(null));
        } else {
          history.push(paths.wallet());
        }
        return;
      }
      // otherwise go home
      history.push(paths.home);
    });
  },

  getCurrentUser: () => {
    const promise = millieApi.authWhoami();
    return { type: Types.GET_CURRENT_USER, promise };
  },

  setCurrentUser: (currentUser, isMasquerading=false, identify=false) => {
    if (__IS_CLIENT__) {
      if (identify) {
        analytics.identify(currentUser);
      }
      if (currentUser) {
        document.body.classList.remove('signed-out');
        document.body.classList.add('signed-in');
      }
    }
    return { type: Types.SET_CURRENT_USER, currentUser, isMasquerading };
  },

  signOut: () => {
    window.localStorage.clear();
    const promise = millieApi.authLogout();
    promise.then(() => {
      window.location.href = paths.home;
    });
    return { type: Types.SIGN_OUT, promise };
  },

  signup: (email, password, firstName, lastName, postalCode, {isClaiming, ...params} = {}) => {
    return (dispatch, getState) => {
      const isMissingNonprofit = isClaiming && !params.nonprofitId;
      const promise = isMissingNonprofit
        ? Promise.reject(utils.mockServerValidations({nonprofitId: ['required']}))
        : millieApi.authRegister(email, password, firstName, lastName, postalCode, params);
      promise.then(({currentUser, mfaRequired}) => {
        if (mfaRequired) {
          return history.push(paths.mfa());
        }
        dispatch(Actions.setCurrentUser(currentUser, false, true));
        dispatch(Actions.redirectAfterLogin());
      });
      return dispatch({ type: Types.SIGN_UP, promise });
    };
  },

  pwLogin: (email, password) => (dispatch, getState) => {
    const promise = millieApi.authLogin({email, password});
    promise.then(({currentUser, mfaRequired}) => {
      if (mfaRequired) {
        return history.push(paths.mfa());
      }
      dispatch(Actions.setCurrentUser(currentUser, false, true));
      dispatch(Actions.redirectAfterLogin());
    });
    return dispatch({ type: Types.PW_LOGIN, promise });
  },

  /** Requests the Millie SAML login address from the Millie web API (for
   *  instance `/saml/the-mouse-federation/login`) then sends the browser to
   *  that route. */
  ssoLogin: (email) => (dispatch, getState) => {
    const promise = millieApi.authSsoLogin(email);
    promise.then(({loginPath}) => {
      window.location.pathname = loginPath;
    });
    return dispatch({ type: Types.SSO_LOGIN, promise });
  },

  resendEmailVerification: () => {
    const promise = millieApi.emailVerificationsResend();
    return { type: Types.RESEND_EV, promise };
  },

  sendPwResetLink: (email) => {
    const promise = millieApi.authResetPassword(email);
    return { type: Types.SEND_PW_RESET, promise };
  },

  resendInviteLink: (email) => {
    const promise = millieApi.employeesInviteResend(email);
    return { type: Types.RESEND_EMP_INVITE, promise };
  },

  stopMasquerading: () => (dispatch, getState) => {
    const promise = millieApi.authStopMasquerading();
    promise.then(() => {
      window.location.href = paths.home;
    });
  },

  employeeEmailSignup: (email) => {
    const promise = millieApi.authEmployeeEmailSignup(email);
    promise.then(({isSso, loginPath}) => {
      if (isSso && loginPath) window.location.href = loginPath;
    });
    return { type: Types.EMP_EMAIL_SIGNUP, promise };
  },

};

export {
  Types,
};

export default Actions;
